import { useState } from 'react';
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Stack, Typography } from '@mui/material';

import MainCard from 'components/MainCard';
import useApi from 'hooks/useApi';
import { errorMessage, isOptionalConsent, unwrapEnvelope } from 'components/legal/legalUtils';
import { ClientLegalOverview, CONSENT_TYPE_LABELS, ConsentType, LegalConsentSettings } from 'types/legal';

const people = (n: number) => `${n} ${n === 1 ? 'person' : 'people'}`;

interface MissingConsentCardProps {
  clientUid: string;
  clientName: string;
  // The saved settings — the sign-out action only makes sense once a checkbox is on.
  consent: LegalConsentSettings;
  missing: ClientLegalOverview['missing'];
  onViewReport: () => void;
  onSignedOut: () => void;
}

const MissingConsentCard = ({ clientUid, clientName, consent, missing, onViewReport, onSignedOut }: MissingConsentCardProps) => {
  const { signOutUnconsentedUsers, showToast } = useApi();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  const anyEnabled = consent.privacyTerms.enabled || consent.sms.enabled;
  const total = Number(missing?.total) || 0;
  const population = Number(missing?.people) || 0;

  const handleSignOut = async () => {
    setSigningOut(true);
    try {
      const data = unwrapEnvelope<{ signedOut: number }>(await signOutUnconsentedUsers(clientUid));
      showToast(`${people(Number(data?.signedOut) || 0)} signed out`, 'success');
      setConfirmOpen(false);
      onSignedOut();
    } catch (err: any) {
      showToast(errorMessage(err, 'Could not sign people out.'), 'error');
    } finally {
      setSigningOut(false);
    }
  };

  const countLine = (type: ConsentType) => {
    const count = missing?.[type];
    if (count === null || count === undefined) return 'Checkbox is switched off';
    // SMS is optional at sign-in: not agreeing is a choice, and those people just aren't texted.
    if (isOptionalConsent(type)) return `${count} of ${people(population)} not agreed (not texted)`;
    return `${count} of ${people(population)} missing`;
  };

  return (
    <>
      <MainCard
        title="Who hasn't accepted"
        subheader={`People in ${clientName} with no consent on record for a checkbox that is switched on.`}
      >
        <Grid container spacing={2}>
          {(['PRIVACY_TERMS', 'SMS'] as ConsentType[]).map((type) => (
            <Grid item xs={12} sm={6} key={type}>
              <Box sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1.5, height: '100%' }}>
                <Typography variant="subtitle1">{CONSENT_TYPE_LABELS[type]}</Typography>
                <Typography
                  variant="h4"
                  component="p"
                  sx={{
                    mt: 0.5,
                    color: missing?.[type] && !isOptionalConsent(type) ? 'error.main' : 'text.secondary',
                    fontWeight: missing?.[type] && !isOptionalConsent(type) ? 600 : 400
                  }}
                >
                  {countLine(type)}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
          <Button variant="outlined" color="secondary" onClick={onViewReport}>
            View report
          </Button>
          {anyEnabled && (
            <Button variant="outlined" color="error" onClick={() => setConfirmOpen(true)} disabled={total === 0}>
              Sign out users who haven't accepted ({total})
            </Button>
          )}
        </Stack>
      </MainCard>

      <Dialog
        open={confirmOpen}
        onClose={() => !signingOut && setConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        aria-labelledby="consent-signout-title"
      >
        <DialogTitle id="consent-signout-title">Sign out {people(total)} who haven't accepted?</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            They're missing a consent that {clientName} now requires.
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, my: 0, '& li': { mb: 0.75 } }}>
            <li>
              <Typography variant="body1">They're signed out of the app, dashboard and weblink.</Typography>
            </li>
            <li>
              <Typography variant="body1">They'll see the checkboxes at their next login.</Typography>
            </li>
            <li>
              <Typography variant="body1">Anyone mid-transaction in the app has to reopen it. Queued offline uploads are kept.</Typography>
            </li>
          </Box>
          <Typography variant="body2" color="warning.dark" sx={{ mt: 1.5, fontWeight: 600 }}>
            Best done outside shift hours.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" color="secondary" onClick={() => setConfirmOpen(false)} disabled={signingOut}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleSignOut}
            disabled={signingOut}
            startIcon={signingOut ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Sign out {people(total)}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MissingConsentCard;
